import { Component, computed, inject, input, resource } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { KENDO_DROPDOWNBUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_GRID } from '@progress/kendo-angular-grid';
import { KENDO_CARD } from '@progress/kendo-angular-layout';
import { AbstractGridComponent } from '@unity/abstract/abstract-grid.component';
import {
  FormInstanceDetailedDto,
  FormInstancesClient,
  FormScheduleDetailedDto,
  FormTemplateClient,
  UnityWorkflowState,
} from '@unity/clients';
import { TypedDirective } from '@unity/directives/typed.directive';
import { DateTimezonePipe } from '@unity/pipes/date-timezone.pipe';
import { FormSchemaService } from '@unity/services/form-schema.service';
import { GridSharedComponent } from '@unity/shared/composed/grid-shared/grid-shared.component';
import { ChipWorkflowStateSharedComponent } from '@unity/shared/presentation/chip-workflow-state-shared/chip-workflow-state-shared.component';
import { SortConfig, SurveyJSProperties } from '@unity/utils/types';
import { QuestionCheckboxModel, QuestionDropdownModel, QuestionRadiogroupModel, QuestionTagboxModel, QuestionTextModel } from 'survey-core';
import { GridcellCurrencySharedComponent } from '../presentation/gridcell-currency-shared/gridcell-currency-shared.component';
import { LinkGridcellSharedComponent } from '../presentation/link-gridcell-shared/link-gridcell-shared.component';

@Component({
  selector: 'unity-grid-form-response-shared',
  standalone: true,
  imports: [
    KENDO_GRID,
    KENDO_CARD,
    KENDO_DROPDOWNBUTTON,
    GridSharedComponent,
    ChipWorkflowStateSharedComponent,
    TypedDirective,
    LinkGridcellSharedComponent,
    GridcellCurrencySharedComponent,
    DateTimezonePipe,
  ],
  templateUrl: './grid-form-response-shared.component.html',
  styleUrl: './grid-form-response-shared.component.scss',
})
export class GridFormResponseSharedComponent extends AbstractGridComponent<FormInstanceDetailedDto> {
  private readonly formInstancesClient = inject(FormInstancesClient);
  private readonly formSchemasClient = inject(FormTemplateClient);
  private readonly formSchemaService = inject(FormSchemaService);

  /* Inputs */
  public readonly $agreementId = input.required<number>({ alias: 'agreementId' });
  public readonly $formSchedule = input.required<FormScheduleDetailedDto>({ alias: 'formSchedule' });

  /* Resources */
  protected override resource = rxResource({
    params: () => {
      const query = this.$gridifyQuery();
      const agreementId = this.$agreementId();
      const formScheduleId = this.$formSchedule().id;
      return query && agreementId && formScheduleId ? { query, agreementId, formScheduleId } : undefined;
    },
    stream: ({ params: { query, agreementId, formScheduleId } }) =>
      this.formInstancesClient.getAllForAgreementAsDetailed(
        agreementId,
        formScheduleId,
        query.page,
        query.pageSize,
        query.orderBy,
        query.filter
      ),
  });

  private readonly formSchemaResource = rxResource({
    params: () => {
      const formSchedule = this.$formSchedule();
      const formSchemaId = formSchedule?.form_schema.id;
      return formSchemaId ? { formSchemaId } : undefined;
    },
    stream: ({ params: { formSchemaId } }) => this.formSchemasClient.getAllAsDetailed(formSchemaId),
  });

  private readonly formSchemaModelResource = resource({
    params: () => {
      const formSchema = this.formSchemaResource.value();
      const formSchemaData = formSchema?.data;
      return formSchemaData ? { formSchemaData } : undefined;
    },
    loader: ({ params: { formSchemaData } }) => this.formSchemaService.surveyModel(formSchemaData),
  });

  /* Computed */
  protected readonly $columns = computed(() => {
    if (!this.formSchemaModelResource.hasValue) {
      return [];
    }
    const model = this.formSchemaModelResource.value();
    if (!model) {
      return [];
    }
    const allQuestions = model.getAllQuestions();
    const visibleQuestions = allQuestions.filter(x => x[SurveyJSProperties.UnityIsGridQuestion] === true);
    const result = visibleQuestions.map(x => {
      const inputType = x['inputType'];
      const { name, title } = x.getPlainData();
      const questionType = x.getType();

      if (
        x instanceof QuestionDropdownModel ||
        x instanceof QuestionRadiogroupModel ||
        x instanceof QuestionCheckboxModel ||
        x instanceof QuestionTagboxModel
      ) {
        return {
          questionType: questionType,
          inputType: inputType,
          name: `${name}`,
          title: title,
          choices: x.choices,
          mapDisplayValue: true,
        };
      }

      if (x instanceof QuestionTextModel) {
        return { questionType: questionType, inputType: inputType, name: `${name}`, title: title, maskType: x.maskType };
      }

      return { questionType: questionType, inputType: inputType, name: `${name}`, title: title };
    });

    return result;
  });

  /* Data */
  protected defaultSort: SortConfig[] = [
    {
      field: 'created_dt',
      dir: 'desc',
    },
  ];

  protected readonly UNITY_WORKFLOW_STATE_DRAFT = UnityWorkflowState.Draft;

  /* Methods */
  protected getDisplayValue(value: string | string[], choices: { value: string; text: string }[]) {
    if (Array.isArray(value)) {
      return value
        .map(v => choices.find(x => x.value === v)?.text)
        .filter(Boolean)
        .join(', ');
    }
    return choices.find(x => x.value === value)?.text;
  }
}
